import { useState, useEffect, useRef } from 'react';
import { X, Loader2 } from 'lucide-react';
import {
  OnboardingButtonVariant,
  type OnboardingStep,
  type OnboardingAction,
} from '@config/onboarding-flows';
import { cn } from '@lib/utils';
import { useSwipeNavigation } from '@hooks/useSwipeNavigation';

export interface OnboardingCarouselProps {
  steps: OnboardingStep[];
  dismissible: boolean;
  onComplete: () => void;
  onDismiss: (stepIndex: number) => void;
  onStepAction: (action: OnboardingAction, stepIndex: number) => void;
  /** Notifies parent of step changes (used for accurate dismiss analytics) */
  onStepChange?: (stepIndex: number) => void;
}

export const OnboardingCarousel = ({
  steps,
  dismissible,
  onComplete,
  onDismiss,
  onStepAction,
  onStepChange,
}: OnboardingCarouselProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const titleRef = useRef<HTMLHeadingElement>(null);

  const isInitialRender = useRef(true);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
    } else {
      titleRef.current?.focus();
    }
  }, [currentStep]);

  const step = steps[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;

  const [isActionPending, setIsActionPending] = useState(false);

  const handleNext = () => {
    if (isActionPending) return;
    if (isLast) {
      if (step.onNext) {
        setIsActionPending(true);
        onStepAction(step.onNext, currentStep);
      } else {
        onComplete();
      }
    } else {
      const next = currentStep + 1;
      setCurrentStep(next);
      onStepChange?.(next);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      const prev = currentStep - 1;
      setCurrentStep(prev);
      onStepChange?.(prev);
    }
  };

  const nextLabel = step.nextLabel ?? 'Next';
  const nextIsPrimary = step.nextVariant === OnboardingButtonVariant.Primary;

  const swipeHandlers = useSwipeNavigation(handleNext, handleBack, isActionPending);

  return (
    <div className="relative flex flex-col gap-5 px-5 pb-8 pt-5" {...swipeHandlers}>
      {/* Dismiss button */}
      {dismissible && (
        <button
          onClick={() => {
            onDismiss(currentStep);
          }}
          className="absolute right-3 top-3 z-10 flex size-6 items-center justify-center text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mb-teal-ink focus-visible:ring-offset-2"
          aria-label="Close"
        >
          <X className="size-3.5" />
        </button>
      )}

      {/* Step image */}
      {step.image && (
        <div className="flex h-32 items-center justify-center overflow-hidden">
          <img
            src={step.image}
            alt={step.imageAlt ?? ''}
            className="mx-auto max-h-full object-contain"
          />
        </div>
      )}

      {/* Step text */}
      <div className="space-y-2.5 text-center">
        <h2
          ref={titleRef}
          tabIndex={-1}
          className="text-lg font-bold text-black font-livvic outline-none"
        >
          {step.title}
        </h2>
        <p className="whitespace-pre-line text-sm leading-snug text-gray-500">{step.body}</p>
      </div>

      {/* Dot indicators */}
      <div role="group" aria-label="Onboarding progress" className="flex justify-center gap-2">
        {steps.map((_, i) => (
          <button
            key={i}
            aria-label={`Go to step ${i + 1}`}
            aria-current={i === currentStep ? 'step' : undefined}
            disabled={isActionPending}
            onClick={() => {
              setCurrentStep(i);
              onStepChange?.(i);
            }}
            className={cn(
              'size-2 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mb-teal-ink focus-visible:ring-offset-2',
              i === currentStep ? 'bg-mb-teal' : 'bg-mb-progress-track',
            )}
          />
        ))}
      </div>

      {/* Navigation buttons */}
      <div className={cn('flex gap-3', isFirst ? 'justify-end' : 'justify-between')}>
        {!isFirst && (
          <button
            onClick={handleBack}
            disabled={isActionPending}
            className="h-12 flex-1 rounded-full border-2 border-mb-progress-track font-fira text-base font-medium tracking-wide text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mb-teal-ink focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {step.backLabel ?? 'Back'}
          </button>
        )}
        <button
          onClick={handleNext}
          disabled={isActionPending}
          className={cn(
            'h-12 rounded-full font-fira text-base font-medium tracking-wide focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mb-teal-ink focus-visible:ring-offset-2',
            isFirst ? 'w-full' : 'flex-1',
            nextIsPrimary
              ? 'bg-mb-teal text-mb-ink shadow-glow-teal'
              : 'border-2 border-mb-progress-track text-gray-700',
          )}
        >
          {isActionPending ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="size-4 animate-spin" />
              <span>{step.loadingLabel ?? 'One sec'}</span>
            </span>
          ) : (
            nextLabel
          )}
        </button>
      </div>
    </div>
  );
};
